'use client';
import React, { useState, useRef, useEffect } from 'react';

interface Message {
  id: string;
  role: 'user' | 'ai';
  text: string;
  loading?: boolean;
}

const INITIAL_MESSAGES: Message[] = [
  {
    id: 'init-user-1',
    role: 'user',
    text: "Quelle est la différence entre 'since' et 'for' ?",
  },
  {
    id: 'init-ai-1',
    role: 'ai',
    text: "Bonne question ! 'Since' indique un point de départ précis dans le temps : \"I have lived here since 2010\" (depuis 2010). 'For' indique une durée : \"I have lived here for 14 years\" (pendant 14 ans). Astuce : si vous pouvez répondre 'quand ?', utilisez 'since'. Si vous pouvez répondre 'combien de temps ?', utilisez 'for'. 😊",
  },
];

export default function GrammarTutor() {
  const [messages, setMessages] = useState<Message[]>(INITIAL_MESSAGES);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || isLoading) return;

    const userMsg: Message = { id: `user-${Date.now()}`, role: 'user', text };
    const loadingMsg: Message = { id: `ai-loading-${Date.now()}`, role: 'ai', text: '...', loading: true };

    setMessages((prev) => [...prev, userMsg, loadingMsg]);
    setInput('');
    setIsLoading(true);

    try {
      // Backend integration point: POST /api/grammar-tutor with { message, history }
      const history = messages.map((m) => ({ role: m.role === 'ai' ? 'assistant' : 'user', content: m.text }));
      const res = await fetch('/api/grammar-tutor', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, history }),
      });

      let aiText = "Je suis désolé, je n'ai pas pu répondre. Vérifiez votre connexion et réessayez.";
      if (res.ok) {
        const data = await res.json();
        aiText = data.reply || aiText;
      }

      setMessages((prev) =>
        prev.map((m) => (m.loading ? { ...m, text: aiText, loading: false } : m))
      );
    } catch {
      setMessages((prev) =>
        prev.map((m) =>
          m.loading
            ? { ...m, text: "Impossible de contacter le tuteur IA. Vérifiez votre connexion.", loading: false }
            : m
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const SUGGESTED = [
    "Quand utiliser \'the\' ?",
    "Différence make vs do ?",
    "Comment former le passé ?",
    "C\'est quoi un faux ami ?",
  ];

  return (
    <section className="py-12 lg:py-16 bg-muted/30">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <p className="section-label mb-3">Tuteur IA</p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-primary mb-3">
              Posez votre question à l&apos;IA
            </h2>
            <p className="text-sm text-secondary">
              Notre tuteur IA répond à vos questions de grammaire en français, simplement et clairement.
            </p>
          </div>

          {/* Chat window */}
          <div className="bg-white border border-border rounded-2xl shadow-card overflow-hidden">
            {/* Chat header */}
            <div className="navy-gradient px-5 py-3.5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-accent/20 border border-accent/30 flex items-center justify-center text-base">🍁</div>
                <div>
                  <p className="text-sm font-semibold text-white">Tuteur IA Grammaire</p>
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                    <p className="text-xs text-white/60">Répond en français</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-white/50">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                <span>Disponible 24h/24</span>
              </div>
            </div>

            {/* Messages */}
            <div className="p-5 space-y-4 h-80 overflow-y-auto">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} fade-in`}
                >
                  {msg.role === 'ai' && (
                    <div className="w-7 h-7 rounded-full bg-primary/10 border border-primary/15 flex items-center justify-center text-sm mr-2 flex-shrink-0 mt-0.5">
                      🍁
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] px-4 py-2.5 text-sm leading-relaxed ${
                      msg.role === 'user' ? 'chat-bubble-user' : 'chat-bubble-ai'
                    }`}
                  >
                    {msg.loading ? (
                      <div className="flex items-center gap-1.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    ) : (
                      msg.text
                    )}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            {/* Suggested questions */}
            {messages.length <= 2 && (
              <div className="px-5 pb-3">
                <p className="text-xs text-muted-foreground mb-2">Questions suggérées :</p>
                <div className="flex flex-wrap gap-2">
                  {SUGGESTED.map((q) => (
                    <button
                      key={`suggested-${q.slice(0, 15)}`}
                      onClick={() => setInput(q)}
                      className="text-xs px-3 py-1.5 bg-muted hover:bg-accent/10 hover:text-accent border border-border rounded-full transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <div className="px-5 pb-5 pt-2 border-t border-border">
              <div className="flex items-end gap-2">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={2}
                  className="input-field resize-none flex-1 text-sm"
                  placeholder="Posez votre question de grammaire en français..."
                  disabled={isLoading}
                />
                <button
                  onClick={sendMessage}
                  disabled={!input.trim() || isLoading}
                  className="w-10 h-10 amber-gradient rounded-xl flex items-center justify-center flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 hover:scale-105 active:scale-95"
                  aria-label="Envoyer"
                >
                  {isLoading ? (
                    <svg className="w-4 h-4 text-primary animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  )}
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-2 text-center">
                Réponses en français • Exemples pratiques • Disponible 24h/24 • Entrée pour envoyer
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}